// The model-set routes: ONE tenant-wide setting that every workspace runs on.
//
//   GET /api/tenant/model-set   usable sets + the stored set, whether the caller may change it
//   PUT /api/tenant/model-set   change it (administrators only)
//
// PERSIST-THEN-RENDER. The write answers with the stored state, and `putTenantModelSet` deliberately
// returns `void`: a caller handed that body would be tempted to draw it. The caller re-reads with the
// GET and renders that. Which sets exist, their names and descriptions are the backend's; nothing in
// the web names a set.

import type { TFunction } from 'i18next';
import { resolveApiError } from './i18nResolve';
import { currentUiLanguage } from '../i18n';
import { ModelApiError, apiFetch, jsonOrThrow, throwIfNotOk } from '../views/model/modelRegistryFetch';

/** Never wait indefinitely on a settings read or write. */
export const MODEL_SET_TIMEOUT_MS = 15_000;

export interface ModelSetOption {
  id: string;
  display_name: string;
  description: string;
}

export interface ModelSetHistoryEntry {
  at: string;
  from: string | null;
  to: string;
}

export interface TenantModelSet {
  sets: ModelSetOption[];
  default_id: string;
  /** The stored set. */
  set_id: string;
  /** False while nothing has been chosen and the environment default applies. */
  configured: boolean;
  updated_at: string | null;
  /** False for everyone but administrators. */
  history: ModelSetHistoryEntry[];
}

const localeQuery = (): string => `?locale=${encodeURIComponent(currentUiLanguage())}`;
const JSON_HEADERS = { 'Content-Type': 'application/json' };

export async function fetchTenantModelSet(signal?: AbortSignal): Promise<TenantModelSet> {
  return jsonOrThrow<TenantModelSet>(
    await apiFetch(`/tenant/model-set${localeQuery()}`, MODEL_SET_TIMEOUT_MS, { signal }),
  );
}

export async function putTenantModelSet(setId: string): Promise<void> {
  await throwIfNotOk(
    await apiFetch('/tenant/model-set', MODEL_SET_TIMEOUT_MS, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify({ set_id: setId }),
    }),
  );
}

/**
 * A failed read or write as copy the user can act on, never a code, a status or a body. Server codes
 * resolve through `resolveApiError` (`errors:model_set_*`, `forbidden`); the two client-side codes
 * the transport raises have no `errors:` entry by design and get owned prose here.
 */
export function modelSetFailureMessage(e: unknown, t: TFunction): string {
  if (e instanceof ModelApiError) {
    if (e.code === 'request_timeout') return t('common:modelSet.errorTimeout');
    if (e.code === 'network_error') return t('common:modelSet.errorNetwork');
    const resolved = resolveApiError({ error: e.code, error_args: e.error_args });
    if (resolved) return resolved;
  }
  console.error('[modelSet] request failed:', e);
  return t('common:modelSet.errorGeneric');
}
